import { useMemo } from 'react';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import { cx } from '@platform/core/lib/cx';
import { controlOf, isPerson } from '@platform/core/lib/fieldTypes';
import type { IFieldRendererProps } from './IFieldRendererProps';
import { fieldControlId } from './fieldControlId';
import { choiceWidth } from './options/choiceWidth';
import { RefOpenLink } from './RefOpenLink';
import { resolveControl } from './registry';
import { RestrictedValue } from './RestrictedValue';
import './fields.css';

/**
 * Поле форми (UIUX/forms/field-grid-and-widths): підпис 11px ВЕЛИКИМИ (рядки модуля → визначення), * для обов'язкових, у полі-посиланні з
 * вибраним записом — « · Відкрити ↗»; ширина на сітці S/M/L (макет групи → явна ширина → довгі підписи варіантів мовою інтерфейсу → тип),
 * особа — окремим рядком; контрол із реєстру; помилка валідації під полем і рамка --danger у контролі. Поле з персональними даними, яке
 * сервер приховав (`restricted`), — без значення й без контрола: RestrictedValue. Id контрола — за поверхнею поля (fieldControlId), тож ключовий
 * рядок і група форми з тим самим полем не дублюють id на сторінці запису.
 */
export function FieldRenderer(props: IFieldRendererProps) {
  const { field, rt, ws, value, onChange, readOnly, error, bare, size, prefix, record, width, ownLine, placeholder, autoFocus, compact, restricted, idPrefix } = props;
  const strings = useModuleStrings();
  const Control = resolveControl(controlOf(field));
  const id = fieldControlId(idPrefix ?? 'f', rt.key, field.key);
  const labels = useMemo(() => (field.options ?? []).map((o) => strings.option(rt.key, field.key, o.value, o.label)), [field, rt.key, strings]);
  const control = restricted ? <RestrictedValue id={id} prefix={bare ? prefix : undefined} compact={bare && !prefix} /> : (
    <Control id={id} value={value as never} onChange={(v: unknown) => onChange(field.key, v)} readOnly={readOnly} field={field} rt={rt} ws={ws}
      size={size} prefix={prefix} record={record} invalid={Boolean(error)} placeholder={placeholder} autoFocus={autoFocus} compact={compact} />
  );
  if (bare) return control;
  const refId = !restricted && (field.type === 'ref' || field.type === 'lookup') && typeof value === 'string' && value ? value : '';
  return (
    <div className={cx('field', `field--${width ?? choiceWidth(field, labels)}`, (ownLine ?? isPerson(field)) && 'field--own-line')}>
      <label className="field__label fc-label" htmlFor={id}>
        {strings.field(rt.key, field.key, field.label)}{field.required && <span className="req"> *</span>}
        {refId && <RefOpenLink field={field} recordId={refId} />}
      </label>
      {control}
      {error && <span className="field__error">{error}</span>}
    </div>
  );
}
